// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * STORAGE_KEYS
 * Single source of truth for all localStorage key strings.
 * Import this file instead of using magic strings to prevent typos and silent save corruption.
 *
 * Dynamic keys: use template literals with the provided prefix constants, e.g.
 *   `${STORAGE_KEYS.SAVE_SLOT_PREFIX}${n}`
 */
const STORAGE_KEYS = Object.freeze({
  // Core state
  CREDITS: 'credits',
  FUEL: 'antimatterFuel',
  MAX_FUEL_CAPACITY: 'maxFuelCapacity',
  TANK_UPGRADES: 'tankUpgrades',
  WEAPONS: 'weapons',
  UNLOCKED_WEAPON_SLOTS: 'unlockedWeaponSlots',
  SHIP_SYSTEMS: 'shipSystems',
  CARGO: 'cargo',
  PURCHASED_UPGRADES: 'purchasedUpgrades',
  INSTALLED_MODULES: 'installedModules',
  UNIVERSE_STATE: 'universeState',

  // Crew
  CREW: 'starship_crew',
  CREW_TRAINING: 'starship_crew_training',
  CREW_CONVERSATIONS: 'starship_crew_conversations',
  CREW_GOALS: 'starship_crew_goals',
  CREW_RELATIONSHIPS: 'starship_crew_relationships',
  RECREATION: 'starship_recreation',
  LAST_PAYDAY: 'lastPayday',
  PAYDAY_HISTORY: 'paydayHistory',

  // Navigation
  CURRENT_LOCATION: 'currentLocation',
  DISCOVERED_SYSTEMS: 'discoveredSystems',
  SCANNED_SYSTEMS: 'scannedSystems',
  STORED_SCAN_RESULTS: 'storedScanResults',
  LAST_SCANNED_SYSTEM: 'lastScannedSystem',
  NAVIGATION_MODE: 'navigationMode',
  QUICK_TRAVEL_BOOKMARKS: 'quickTravelBookmarks',
  SELECTED_SYSTEM: 'selectedSystem',
  IN_ORBIT: 'inOrbit',
  DISTANCE_POSITIONS: 'distancePositions',
  STAR_SYSTEMS: 'starSystems',

  // Game managers
  GAME_TIME: 'starship_gametime',
  RESOURCES: 'starship_resources',
  UPGRADES: 'starship_upgrades',
  MARKET_INVENTORY: 'starship_market_inventory',
  REPUTATION: 'starship_reputation',

  // Save/Load
  AUTOSAVE: 'starship_console_autosave',
  CURRENT_SLOT: 'starship_console_current_slot',
  // Dynamic save slots — use as: `${STORAGE_KEYS.SAVE_SLOT_PREFIX}${n}`
  SAVE_SLOT_PREFIX: 'starship_console_save_slot_',

  // UI & Settings
  TUTORIAL_COMPLETED: 'tutorialCompleted',
  SHIP_NAME: 'shipName',
  SHIP_VOICE: 'shipVoice',
  SHIP_VOICE_VOLUME: 'shipVoiceVolume',
  SHIP_VOICE_ENABLED: 'shipVoiceEnabled',
  SOUND_EFFECTS_VOLUME: 'soundEffectsVolume',
  SOUND_EFFECTS_ENABLED: 'soundEffectsEnabled',
  SOUND_EFFECTS_PACK: 'soundEffectsPack',
  SOUND_PACK: 'soundPack',
  MASTER_VOLUME: 'masterVolume',
  DIFFICULTY: 'difficulty',
  PURCHASE_CONFIRM_THRESHOLD: 'purchaseConfirmThreshold',
  WARP_SKIP_ENABLED: 'warpSkipEnabled',
  AUTOSAVE_INTERVAL: 'autosaveInterval',
  HAS_PLAYED_INIT_VOICE: 'hasPlayedInitVoice',
  CURRENT_RANK: 'currentRank',
  TUTORIAL_HINTS: 'tutorialHints',
  DEV_TOOLS_SAVED_STATES: 'devToolsSavedStates',
  CLOAK_ACTIVE: 'cloakActive',
  JAMMING_ACTIVE: 'jammingActive',

  // Logging & achievements
  CAPTAINS_LOG: 'captainsLog',
  GAME_EVENT_LOG: 'gameEventLog',
  ACHIEVEMENTS: 'achievements',
  ACHIEVEMENTS_STATS: 'achievements_stats',
  UNIVERSE_VALIDATION_LOG: 'universe_validation_log',

  // Stats
  SYSTEMS_VISITED: 'systemsVisited',
  COMBAT_WINS: 'combatWins',
  TOTAL_CREDITS_EARNED: 'totalCreditsEarned',
  MISSIONS_COMPLETED: 'missionsCompleted',
  ALIENS_ENCOUNTERED: 'aliensEncountered',
  DISTANCE_TRAVELED: 'distanceTraveled',
  VISITED_SYSTEMS: 'visitedSystems',
  RECENT_SYSTEMS: 'recentSystems',
  ENCOUNTERED_ALIENS: 'encounteredAliens',
  BATTLES_WON: 'battlesWon',
  BATTLES_LOST: 'battlesLost',
  TOTAL_DISTANCE_TRAVELED: 'totalDistanceTraveled',
  TRADES_COMPLETED: 'tradesCompleted',
  PLANETS_EXPLORED: 'planetsExplored',
  CURRENT_SYSTEM_NAME: 'currentSystemName',

  // Crew life settings
  CREW_DETAIL_LEVEL: 'crewDetailLevel',
  CREW_ACTIVITY_FREQUENCY: 'crewActivityFrequency',
  CREW_MINIGAME_PROMPTS: 'crewMinigamePrompts',
  CREW_NOTIFICATIONS: 'crewNotifications',
});

export default STORAGE_KEYS;
